import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Icon from "components/AppIcon";

// Validates the reset token from URL query params (?token=...).
// Calls onValidated(token) if token is present, onInvalid() otherwise.
export default function TokenValidator({ onValidated, onInvalid }) {
  const [status, setStatus] = useState("checking"); // checking | invalid
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams?.get('token');
    if (token) {
      setStatus("valid");
      onValidated?.(token);
    } else {
      setStatus("invalid");
      onInvalid?.();
    }
  }, []);

  if (status === "checking") {
    return (
      <div className="text-center py-10">
        <div className="relative w-16 h-16 mx-auto mb-5">
          <div className="absolute inset-0 rounded-full border-2 border-teal-500/20" />
          <div className="absolute inset-0 rounded-full border-t-2 border-teal-400 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon name="Lock" size={20} color="#2dd4bf" />
          </div>
        </div>
        <p className="text-white font-medium font-heading text-sm mb-1">Verifying reset link</p>
        <p className="text-slate-500 text-xs font-body">Please wait while we validate your token...</p>
        <div className="mt-6 space-y-2.5 px-4">
          <div className="h-10 rounded-xl bg-white/5 animate-pulse" />
          <div className="h-10 rounded-xl bg-white/5 animate-pulse" />
          <div className="h-11 rounded-xl bg-white/5 animate-pulse" />
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/25 flex items-center justify-center mx-auto mb-5">
          <Icon name="XCircle" size={32} color="#f87171" />
        </div>
        <h2 className="text-xl font-bold text-white font-heading mb-2">Link expired or invalid</h2>
        <p className="text-slate-400 text-sm font-body mb-2 leading-relaxed">
          This password reset link has expired or has already been used.
        </p>
        <p className="text-slate-500 text-xs font-body mb-6">
          Reset links are valid for 1 hour and can only be used once.
        </p>
        <Link
          to="/forgot-password-screen"
          className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm font-caption transition-all duration-200 shadow-lg shadow-teal-900/30"
        >
          Request a new link
          <Icon name="ArrowRight" size={16} />
        </Link>
        <div className="mt-4">
          <Link
            to="/login-page"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors font-caption"
          >
            <Icon name="ArrowLeft" size={13} />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
